import { useMemo, useReducer, useRef, useState } from "react";
import STUDIO_CSS from "./studioStyle";
import {
  PALETTE,
  TYPE_META,
  SECTION_ICONS,
  HAS_OPTS,
  nid,
  modelFromNoteType,
  emptyModel,
  noteTypePayload,
  fieldStorageKeys,
  CARE_PLAN_PARTS,
  carePlanPartsOf,
} from "./schema";
import { createNoteType, editNoteType } from "../../../../services/note";
import { useForm } from "@mantine/form";
import StudioSectionsForm from "../../../../components/NoteModal/StudioSectionsForm";
import useNoteModalStyles from "../../../../components/NoteModal/styles";

/* ── tiny inline icon set ── */
const IC = {
  rows: '<rect x="3" y="4" width="18" height="7" rx="1.5"/><rect x="3" y="13" width="18" height="7" rx="1.5"/>',
  text: '<path d="M4 7V5h16v2M9 5v14M7 19h4"/>',
  para: '<path d="M17 6H9a3 3 0 0 0 0 6h4M13 6v14M7 6h2"/>',
  num: '<path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18"/>',
  date: '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>',
  time: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  drop: '<path d="M6 9l6 6 6-6"/>',
  single: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.5" fill="currentColor"/>',
  multi: '<rect x="3" y="3" width="18" height="18" rx="3"/><path d="M8 12l3 3 5-6"/>',
  toggle: '<rect x="2" y="7" width="20" height="10" rx="5"/><circle cx="16" cy="12" r="3" fill="currentColor"/>',
  chips: '<path d="M20.6 8.4 12 17l-8.6-8.6a4 4 0 0 1 5.7-5.7L12 5l2.9-2.3a4 4 0 0 1 5.7 5.7z"/>',
  client: '<circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/>',
  staff: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/>',
  facility: '<path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6"/>',
  notif: '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.9 1.9 0 0 0 3.4 0"/>',
  calc: '<rect x="4" y="2" width="16" height="20" rx="2"/><path d="M8 6h8M8 10h2M14 10h2M8 14h2M14 14h2M8 18h2M14 18h2"/>',
  careplan: '<path d="M9 3h6a1 1 0 0 1 1 1v1h1a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h1V4a1 1 0 0 1 1-1z"/><path d="M12 11.5c-.6-1.2-2.5-1.1-2.5.4 0 1 1.2 1.8 2.5 3 1.3-1.2 2.5-2 2.5-3 0-1.5-1.9-1.6-2.5-.4z"/>',
  grip: '<circle cx="9" cy="6" r="1.4"/><circle cx="15" cy="6" r="1.4"/><circle cx="9" cy="12" r="1.4"/><circle cx="15" cy="12" r="1.4"/><circle cx="9" cy="18" r="1.4"/><circle cx="15" cy="18" r="1.4"/>',
  eye: '<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>',
  trash: '<path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  arrowUp: '<path d="M12 19V5M5 12l7-7 7 7"/>',
  arrowDown: '<path d="M12 5v14M19 12l-7 7-7-7"/>',
  copy: '<rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>',
  tag: '<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>',
  pin: '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>',
  users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/>',
  doc: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/>',
  cross: '<path d="M11 2a2 2 0 0 0-2 2v5H4a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h5v5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-5h5a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2h-5V4a2 2 0 0 0-2-2z"/>',
  shield: '<path d="M18.36 6.64A9 9 0 1 1 5.64 6.64M12 2v10"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  alert: '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/>',
  link: '<path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/>',
  chev: '<path d="M15 18l-6-6 6-6"/>',
  x: '<path d="M18 6 6 18M6 6l12 12"/>',
  upload: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>',
};
const Svg = ({ n, w = 16, s = 2 }) => (
  <svg width={w} height={w} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={s}
    strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: IC[n] || "" }} />
);
const initials = (name = "") =>
  name.replace(/[^A-Za-z ]/g, "").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const DEFAULT_LABEL = {
  text: "Text Field", textarea: "Description", number: "Number", date: "Date", time: "Time",
  dropdown: "Select One", single: "Single Choice", multi: "Multiple Choice", toggle: "Yes / No Question",
  chips: "Severity", client: "Client(s)", staff: "Staff", facility: "Facility",
  notifications: "Notifications", calc: "Calculated Total", careplan: "Use care plan?",
};
const newField = (type) => {
  const base = {
    id: nid(), type, label: DEFAULT_LABEL[type] || "Field", required: false,
    span: type === "textarea" || HAS_OPTS(type) || ["client", "staff", "notifications"].includes(type) ? 12 : 6,
    placeholder: "", help: "", options: [], conditional: null, calc: null,
  };
  if (HAS_OPTS(type)) base.options = type === "chips" ? ["Minor", "Moderate", "Serious", "Critical"] : ["Option 1", "Option 2"];
  if (type === "calc") { base.calc = { formula: "", type: "number" }; base.span = 6; }
  // Outcomes-only by default — the same thing a care plan field did before the
  // parts became selectable, so dragging one in behaves as authors expect.
  if (type === "careplan") base.carePlanParts = ["outcomes"];
  return base;
};

const NO_VALUE_OPS = ["is empty", "is filled", "is not empty", "has response", "is complete", "is shown", "is hidden"];
const needsValue = (op) => !NO_VALUE_OPS.includes(op);
const operatorsFor = (trig) => {
  if (!trig) return ["is", "is not", "is filled", "is empty"];
  if (trig.kind === "section") return ["is empty", "has response", "is complete", "is shown", "is hidden"];
  const t = trig.type;
  if (["client", "staff", "facility", "notifications"].includes(t)) return ["is not empty", "is empty"];
  if (t === "multi") return ["includes", "does not include", "is not empty", "is empty"];
  if (t === "number") return ["is", "is not", "greater than", "less than", "is filled", "is empty"];
  if (["text", "textarea"].includes(t)) return ["is", "is not", "contains", "is filled", "is empty"];
  return ["is", "is not", "is filled", "is empty"];
};

const NoteTypeStudio = ({ note, admins = [], staffList = [], onClose, onSaved, readOnly = false }) => {
  const initial = useMemo(() => (note ? modelFromNoteType(note) : emptyModel()), [note]);
  const model = useRef(initial);
  const [, force] = useReducer((x) => x + 1, 0);
  const rerender = force;
  const dragRef = useRef(null);
  const [sel, setSel] = useState({ kind: "note" });
  const [mode, setMode] = useState("build");
  const [step, setStep] = useState("build");
  const [toast, setToast] = useState("");
  const [saving, setSaving] = useState(false);
  const [apprExpanded, setApprExpanded] = useState(false);
  // Live preview renders through the real create-drawer field component
  // (StudioSectionsForm) so preview fields exactly match note creation. It
  // drives an isolated throwaway Mantine form + the NoteModal styles.
  const previewForm = useForm({ initialValues: {} });
  const { classes: noteModalClasses } = useNoteModalStyles();

  const M = model.current;
  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2600); };

  // ── model helpers ──
  const allFields = () => M.sections.flatMap((s) => s.fields);
  // Where each field's answer is stored — its label, except for a field whose
  // name is reused in another section (allowed) which resolves to
  // "<name> (<section>)". Conditions, formulas and the preview all have to
  // reference this, never the bare label, or two same-named fields would read
  // and write the same value. Recomputed per render: the model is mutated in
  // place, so there's no identity to memoize on.
  const storageKeys = fieldStorageKeys(M);
  const keyOf = (f) => storageKeys[f.id] || f.label;
  const findField = (id) => {
    for (const s of M.sections) { const f = s.fields.find((x) => x.id === id); if (f) return { f, s }; }
    return {};
  };
  // Names only have to be unique WITHIN a section — two sections may each have
  // a "Beginning Balance". Their storage keys are kept apart by
  // fieldStorageKeys(), so same-named fields in different sections no longer
  // share an answer.
  const isDup = (f) => {
    const k = (f.label || "").trim().toLowerCase();
    if (!k) return false;
    const sec = M.sections.find((s) => s.fields.some((x) => x.id === f.id));
    return (
      (sec?.fields || []).filter((x) => (x.label || "").trim().toLowerCase() === k).length > 1
    );
  };
  const triggersFor = (target) => {
    const out = [];
    if (target.kind === "section") {
      const idx = M.sections.findIndex((s) => s.id === target.id);
      M.sections.slice(0, idx).forEach((s) => {
        out.push({ value: "SEC::" + s.id, label: s.title, kind: "section" });
        s.fields.forEach((f) => out.push({ value: keyOf(f), label: keyOf(f), kind: "field", type: f.type }));
      });
    } else {
      const ord = allFields();
      const i = ord.findIndex((x) => x.id === target.f.id);
      const secIdx = M.sections.findIndex((s) => s.fields.some((x) => x.id === target.f.id));
      M.sections.slice(0, secIdx).forEach((s) => out.push({ value: "SEC::" + s.id, label: s.title, kind: "section" }));
      ord.slice(0, i).forEach((f) => out.push({ value: keyOf(f), label: keyOf(f), kind: "field", type: f.type }));
    }
    return out;
  };

  const addSection = (beforeSecId, carePlan) => {
    const s = {
      id: "s" + nid(), title: carePlan ? "Care Plan" : "New Section",
      icon: carePlan ? "careplan" : SECTION_ICONS[M.sections.length % SECTION_ICONS.length],
      ...(carePlan ? { carePlan: true } : {}), cond: null, fields: [],
    };
    const i = beforeSecId ? M.sections.findIndex((x) => x.id === beforeSecId) : M.sections.length;
    M.sections.splice(i < 0 ? M.sections.length : i, 0, s);
    setSel({ kind: "section", id: s.id }); rerender();
  };
  const moveSectionBefore = (id, beforeSecId) => {
    const from = M.sections.findIndex((s) => s.id === id); if (from < 0) return;
    const [m] = M.sections.splice(from, 1);
    let to = beforeSecId ? M.sections.findIndex((s) => s.id === beforeSecId) : M.sections.length;
    if (to < 0) to = M.sections.length;
    M.sections.splice(to, 0, m); rerender();
  };
  const moveSection = (id, dir) => {
    const i = M.sections.findIndex((s) => s.id === id); const j = i + dir;
    if (j < 0 || j >= M.sections.length) return;
    const [m] = M.sections.splice(i, 1); M.sections.splice(j, 0, m); rerender();
  };
  const duplicateSection = (id) => {
    const i = M.sections.findIndex((s) => s.id === id);
    const copy = JSON.parse(JSON.stringify(M.sections[i]));
    copy.id = "s" + nid(); copy.title += " (copy)"; copy.fields.forEach((f) => { f.id = nid(); delete f._src; });
    M.sections.splice(i + 1, 0, copy); setSel({ kind: "section", id: copy.id }); rerender();
  };
  const deleteSection = (id) => {
    model.current.sections = M.sections.filter((x) => x.id !== id);
    if (sel.id === id) setSel({ kind: "note" });
    rerender();
  };
  const addField = (secId, type, beforeId) => {
    const s = M.sections.find((x) => x.id === secId); if (!s) return;
    const nf = newField(type);
    const i = beforeId ? s.fields.findIndex((x) => x.id === beforeId) : -1;
    s.fields.splice(i < 0 ? s.fields.length : i, 0, nf);
    setSel({ kind: "field", id: nf.id }); rerender();
  };
  const moveField = (id, secId, beforeId) => {
    const { f, s } = findField(id); if (!f) return;
    s.fields = s.fields.filter((x) => x.id !== id);
    const dest = M.sections.find((x) => x.id === secId); if (!dest) return;
    const i = beforeId && beforeId !== id ? dest.fields.findIndex((x) => x.id === beforeId) : -1;
    dest.fields.splice(i < 0 ? dest.fields.length : i, 0, f);
    rerender();
  };
  const deleteField = (id) => {
    const { s } = findField(id); if (!s) return;
    s.fields = s.fields.filter((x) => x.id !== id);
    if (sel.id === id) setSel({ kind: "note" });
    rerender();
  };
  const setField = (id, patch) => { const { f } = findField(id); if (f) Object.assign(f, patch); rerender(); };

  // ── drag & drop (native) with live drop-line indicators ──
  // dropRef / secDropRef hold the computed insert target while dragging, set by
  // the drop-line placement below so the drop lands exactly where the yellow
  // guide bar is shown (mirrors the Studio mockup).
  const dropRef = useRef(null);      // { secId, beforeId }
  const secDropRef = useRef(null);   // beforeSecId | null (end)

  const clearFieldDropline = () => {
    const d = document.getElementById("nt-dl"); if (d) d.remove();
    dropRef.current = null;
  };
  const placeFieldDropline = (grid, e) => {
    const existing = document.getElementById("nt-dl"); if (existing) existing.remove();
    const cards = [...grid.querySelectorAll("[data-field]:not(.dragging)")];
    let before = null;
    for (const c of cards) { const r = c.getBoundingClientRect(); if (e.clientY < r.top + r.height / 2) { before = c; break; } }
    const line = document.createElement("div");
    line.className = "dropline"; line.id = "nt-dl";
    if (before) grid.insertBefore(line, before); else grid.appendChild(line);
    dropRef.current = { secId: grid.getAttribute("data-grid"), beforeId: before ? before.getAttribute("data-field") : null };
  };
  const clearSectionDropline = () => {
    const d = document.getElementById("nt-secdl"); if (d) d.remove();
    secDropRef.current = null;
  };
  const placeSectionDropline = (sheet, e) => {
    const existing = document.getElementById("nt-secdl"); if (existing) existing.remove();
    const secs = [...sheet.querySelectorAll("[data-section]:not(.dragging)")];
    let before = null;
    for (const s of secs) { const r = s.getBoundingClientRect(); if (e.clientY < r.top + r.height / 2) { before = s; break; } }
    const line = document.createElement("div");
    line.className = "secdropline"; line.id = "nt-secdl";
    const addBtn = sheet.querySelector(".addsection");
    if (before) sheet.insertBefore(line, before); else sheet.insertBefore(line, addBtn);
    secDropRef.current = before ? before.getAttribute("data-section") : null;
  };
  const clearDroplines = () => { clearFieldDropline(); clearSectionDropline(); };

  const onFieldDrop = (secId, e) => {
    e.preventDefault(); e.stopPropagation();
    const d = dragRef.current; if (!d) { clearFieldDropline(); return; }
    const beforeId = dropRef.current && dropRef.current.secId === secId ? dropRef.current.beforeId : null;
    if (d.kind === "new") addField(secId, d.type, beforeId);
    else if (d.kind === "move") moveField(d.id, secId, beforeId);
    dragRef.current = null; clearFieldDropline();
  };
  const onSheetDrop = (e) => {
    const d = dragRef.current; if (!d || (d.kind !== "newsection" && d.kind !== "movesection")) return;
    e.preventDefault();
    const beforeSecId = secDropRef.current;
    if (d.kind === "newsection") addSection(beforeSecId, d.careplan);
    else moveSectionBefore(d.id, beforeSecId);
    dragRef.current = null; clearSectionDropline();
  };

  // ── validation + save ──
  const validate = () => {
    const issues = [];
    if (!(M.name || "").trim()) issues.push("The note type needs a name.");
    allFields().forEach((f) => { if (!(f.label || "").trim()) issues.push("A field has no name."); });
    // Per section, not per template: reusing a name in a different section is
    // allowed (see isDup).
    M.sections.forEach((s) => {
      const counts = {};
      (s.fields || []).forEach((f) => { const k = (f.label || "").trim().toLowerCase(); if (k) counts[k] = (counts[k] || 0) + 1; });
      Object.entries(counts).forEach(([k, c]) => {
        if (c > 1) issues.push(`“${s.title}” has ${c} fields named “${k}”. Names must be unique within a section.`);
      });
    });
    M.sections.forEach((s) => { if (!s.carePlan && !s.fields.length) issues.push(`Section “${s.title}” has no fields.`); });
    return issues;
  };
  const goSettings = () => {
    const issues = validate();
    if (issues.length) { flash(issues[0]); return; }
    setStep("settings");
  };
  const save = async () => {
    if (M.settings.approvalRequired && !M.settings.allApprovers && !M.settings.approvers.length) {
      flash("Select at least one approver, or choose All admins.");
      return;
    }
    // Same "nobody can use this" check the Availability warning shows, in both
    // modes (empty allow-list, or every staff member unchecked/excluded).
    const availableStaffCount =
      M.settings.allStaff === false
        ? (M.settings.staff || []).length
        : staffList.filter((x) => !(M.settings.staffExcluded || []).includes(x.id)).length;
    if (staffList.length && !availableStaffCount) {
      flash("Check at least one staff member, or turn on Available to all staff.");
      return;
    }
    setSaving(true);
    const payload = noteTypePayload(M);
    let ok = false;
    if (M.id) ok = (await editNoteType(payload)) !== false;
    else { await createNoteType(payload); ok = true; }
    setSaving(false);
    if (ok) { onSaved && onSaved(); onClose && onClose(); }
  };

  const back = () => { if (step === "settings") setStep("build"); else onClose && onClose(); };

  /* ───────────────────────── render pieces ───────────────────────── */
  const secIcon = (icon, w = 14) =>
    icon && icon.indexOf("img:") === 0
      ? <img src={icon.slice(4)} alt="" style={{ width: w, height: w, objectFit: "cover", borderRadius: 3 }} />
      : <Svg n={icon || "tag"} w={w} />;

  const renderPalette = () => (
    <div className="pane rail">
      {PALETTE.map((g) => (
        <div key={g.group}>
          <h3>{g.group}</h3>
          <div className="palette">
            {g.items.map((it) => (
              <div key={it.type} className={`pchip ${g.cls}`} draggable
                onDragStart={() => { dragRef.current = it.type === "section" ? { kind: "newsection", careplan: false } : { kind: "new", type: it.type }; }}
                onDragEnd={() => { dragRef.current = null; clearDroplines(); }}
                onClick={() => { if (it.type === "section") addSection(sel?.kind === "section" ? sel.id : null); }}>
                <span className="ic">{it.type === "careplan" ? <Svg n="careplan" w={15} /> : <Svg n={it.ic} w={15} />}</span>
                {it.name}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="hinttip"><b>Drag</b> a field onto the canvas, or drag cards to reorder. Click a field to edit its label, width, options, conditional rule or formula.</div>
    </div>
  );

  const fieldPreviewLine = (f) => {
    if (f.type === "calc") return "ƒ " + (f.calc?.formula || "—");
    if (HAS_OPTS(f.type)) return (f.options || []).slice(0, 3).join(" · ") + ((f.options || []).length > 3 ? " …" : "");
    if (f.type === "toggle") return "Yes / No";
    if (f.help) return f.help;
    return TYPE_META[f.type]?.label || "";
  };

  const renderCanvas = () => (
    <div className="pane canvas">
      <div className="sheet" data-sheet
        onDragOver={(e) => { const k = dragRef.current?.kind; if (k === "newsection" || k === "movesection") { e.preventDefault(); placeSectionDropline(e.currentTarget, e); } }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) clearSectionDropline(); }}
        onDrop={onSheetDrop}>
        <div className={`ntheader ${sel?.kind === "note" ? "selected" : ""}`} onClick={() => setSel({ kind: "note" })}>
          <div className="tt">{M.name || "Untitled note type"}</div>
          {M.desc ? <div className="ds">{M.desc}</div> : null}
        </div>

        {M.sections.map((sec) => {
          const selS = sel?.kind === "section" && sel.id === sec.id;
          const hasCond = sec.cond && sec.cond.rules.length;
          return (
            <div key={sec.id} className={`section ${selS ? "selected" : ""}`} data-section={sec.id}
              onClick={(e) => { if (e.target.closest("[data-field]") || e.target.closest("[data-stop]")) return; setSel({ kind: "section", id: sec.id }); }}>
              <div className="shead">
                <span className="grip" draggable data-stop
                  onDragStart={(e) => { dragRef.current = { kind: "movesection", id: sec.id }; const secEl = e.currentTarget.closest("[data-section]"); if (secEl) setTimeout(() => secEl.classList.add("dragging"), 0); }}
                  onDragEnd={(e) => { dragRef.current = null; const secEl = e.currentTarget.closest("[data-section]"); if (secEl) secEl.classList.remove("dragging"); clearSectionDropline(); }}><Svg n="grip" w={16} /></span>
                <span className="sico">{secIcon(sec.icon)}</span>
                <input className="stitle" data-stop value={sec.title}
                  onChange={(e) => { sec.title = e.target.value; rerender(); }} />
                {sec.carePlan ? <span className="cpchip"><Svg n="link" w={11} /> Care plan</span> : null}
                <button className={`sfx ${hasCond ? "on" : ""}`} data-stop onClick={() => openSectionCond(sec)}>
                  <Svg n="eye" w={13} /> {hasCond ? "Conditional" : "Add condition"}
                </button>
                <button className="sdel" data-stop title="Delete section" onClick={() => deleteSection(sec.id)}><Svg n="trash" w={15} /></button>
              </div>
              <div className="fgrid" data-grid={sec.id}
                onDragOver={(e) => { if (dragRef.current?.kind === "new" || dragRef.current?.kind === "move") { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add("dragover"); placeFieldDropline(e.currentTarget, e); } }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) { e.currentTarget.classList.remove("dragover"); clearFieldDropline(); } }}
                onDrop={(e) => { e.currentTarget.classList.remove("dragover"); onFieldDrop(sec.id, e); }}>
                {sec.carePlan ? (
                  <div className="cpblock"><div className="h"><Svg n="link" w={13} /> Care Plan</div>
                    <div className="t">Pulls the client's active support-plan outcomes at note time.</div></div>
                ) : null}
                {!sec.fields.length && !sec.carePlan ? <div className="empty">Drop fields here</div> : null}
                {sec.fields.map((f) => {
                  const selF = sel?.kind === "field" && sel.id === f.id;
                  return (
                    <div key={f.id} className={`fcard ${selF ? "selected" : ""} ${isDup(f) ? "dup" : ""}`}
                      style={{ gridColumn: `span ${f.span}` }} data-field={f.id} draggable
                      onDragStart={(e) => { e.stopPropagation(); dragRef.current = { kind: "move", id: f.id }; e.currentTarget.classList.add("dragging"); }}
                      onDragEnd={(e) => { e.currentTarget.classList.remove("dragging"); dragRef.current = null; clearFieldDropline(); }}
                      onClick={(e) => { if (e.target.closest("[data-stop]")) return; setSel({ kind: "field", id: f.id }); }}>
                      <div className="frow">
                        <span className="fgrip"><Svg n="grip" w={15} /></span>
                        <span className="flabel">{f.label || "Untitled"}{f.required ? <span className="freq"> *</span> : null}</span>
                        <span className="fbadges">
                          {isDup(f) ? <span className="fbtn" title="Duplicate name"><Svg n="alert" w={13} /></span> : null}
                          <span className="ftype">{TYPE_META[f.type]?.label || f.type}</span>
                          <button className={`fbtn ${f.conditional?.rules?.length ? "on" : ""}`} data-stop title="Conditional visibility" onClick={() => openFieldCond(f)}><Svg n="eye" w={14} /></button>
                          <button className="fbtn del" data-stop title="Delete" onClick={() => deleteField(f.id)}><Svg n="trash" w={14} /></button>
                        </span>
                      </div>
                      <div className="fprev">{fieldPreviewLine(f)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        <button className="addsection" onClick={() => addSection()}><Svg n="plus" w={15} /> Add section</button>
      </div>
    </div>
  );

  // System (general) note types render this instead of renderCanvas: same
  // section/field layout and the same selection behavior (so the properties
  // panel on the right reflects whatever's clicked), but nothing is
  // draggable, addable, or deletable — no drag handlers, no add/delete/
  // condition buttons, section title as plain text instead of an input.
  const renderCanvasReadOnly = () => (
    <div className="pane canvas">
      <div className="sheet">
        <div className={`ntheader ${sel?.kind === "note" ? "selected" : ""}`} onClick={() => setSel({ kind: "note" })}>
          <div className="tt">{M.name || "Untitled note type"}</div>
          {M.desc ? <div className="ds">{M.desc}</div> : null}
        </div>
        {M.sections.map((sec) => {
          const selS = sel?.kind === "section" && sel.id === sec.id;
          const hasCond = sec.cond && sec.cond.rules.length;
          return (
            <div key={sec.id} className={`section ${selS ? "selected" : ""}`}
              onClick={(e) => { if (e.target.closest("[data-field]")) return; setSel({ kind: "section", id: sec.id }); }}>
              <div className="shead">
                <span className="sico">{secIcon(sec.icon)}</span>
                <span className="stitle" style={{ cursor: "default" }}>{sec.title}</span>
                {sec.carePlan ? <span className="cpchip"><Svg n="link" w={11} /> Care plan</span> : null}
                {hasCond ? <span className="sfx on"><Svg n="eye" w={13} /> Conditional</span> : null}
              </div>
              <div className="fgrid">
                {sec.carePlan ? (
                  <div className="cpblock"><div className="h"><Svg n="link" w={13} /> Care Plan</div>
                    <div className="t">Pulls the client's active support-plan outcomes at note time.</div></div>
                ) : null}
                {!sec.fields.length && !sec.carePlan ? <div className="empty">No fields</div> : null}
                {sec.fields.map((f) => {
                  const selF = sel?.kind === "field" && sel.id === f.id;
                  return (
                    <div key={f.id} className={`fcard ${selF ? "selected" : ""}`} data-field={f.id}
                      style={{ gridColumn: `span ${f.span}`, cursor: "default" }}
                      onClick={(e) => { e.stopPropagation(); setSel({ kind: "field", id: f.id }); }}>
                      <div className="frow">
                        <span className="flabel">{f.label || "Untitled"}{f.required ? <span className="freq"> *</span> : null}</span>
                        <span className="fbadges">
                          <span className="ftype">{TYPE_META[f.type]?.label || f.type}</span>
                          {f.conditional?.rules?.length ? <span className="fbtn on"><Svg n="eye" w={14} /></span> : null}
                        </span>
                      </div>
                      <div className="fprev">{fieldPreviewLine(f)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // open conditional editors: ensure object exists then select owner
  const openFieldCond = (f) => {
    const trig = triggersFor({ kind: "field", f });
    setSel({ kind: "field", id: f.id });
    if (!trig.length) { rerender(); flash("This is the first field — nothing earlier to gate it on."); return; }
    f.conditional = f.conditional || { mode: "all", rules: [] };
    if (!f.conditional.rules.length) { const t = trig[0]; f.conditional.rules.push({ kind: t.kind, field: t.value, op: operatorsFor(t)[0], value: "" }); }
    rerender();
  };
  const openSectionCond = (sec) => {
    const trig = triggersFor({ kind: "section", id: sec.id });
    setSel({ kind: "section", id: sec.id });
    if (!trig.length) { rerender(); flash("A section can only be gated by a field or section above it."); return; }
    sec.cond = sec.cond || { mode: "all", rules: [] };
    if (!sec.cond.rules.length) { const t = trig[0]; sec.cond.rules.push({ kind: t.kind, field: t.value, op: operatorsFor(t)[0], value: "" }); }
    rerender();
  };

  const conditionalEditor = ({ owner, keyName, target }) => {
    const trig = triggersFor(target);
    const c = owner[keyName];
    const noun = target.kind === "section" ? "section" : "field";
    if (!trig.length) return (<div className="psec"><div className="ph"><Svg n="eye" w={13} /> Conditional visibility</div><div className="note">Nothing earlier to depend on yet.</div></div>);
    const addRule = () => { owner[keyName] = c || { mode: "all", rules: [] }; const t = trig[0]; owner[keyName].rules.push({ kind: t.kind, field: t.value, op: operatorsFor(t)[0], value: "" }); rerender(); };
    return (
      <div className="psec">
        <div className="ph"><Svg n="eye" w={13} /> Conditional visibility</div>
        {!c || !c.rules.length ? (
          <>
            <div className="note" style={{ marginBottom: 8 }}>Always visible.</div>
            <button className="ghost" style={{ width: "100%" }} onClick={addRule}><Svg n="plus" w={13} /> Add a show-when rule</button>
          </>
        ) : (
          <>
            <div className="note" style={{ marginBottom: 6 }}>Show this {noun} when{" "}
              <span className="modepick">
                <button aria-pressed={c.mode === "all"} onClick={() => { c.mode = "all"; rerender(); }}>all</button>
                <button aria-pressed={c.mode === "any"} onClick={() => { c.mode = "any"; rerender(); }}>any</button>
              </span>{" "}match:</div>
            {c.rules.map((r, i) => {
              const t = trig.find((x) => x.value === r.field) || null;
              const ops = operatorsFor(t);
              const valField = t && t.kind === "field" ? allFields().find((x) => keyOf(x) === t.value) : null;
              return (
                <div className="rule" key={i}>
                  <div className="rr">
                    <select className="inp" value={r.field} style={{ flex: 1 }}
                      onChange={(e) => { r.field = e.target.value; const nt = trig.find((x) => x.value === r.field); r.kind = nt ? nt.kind : r.kind; r.op = operatorsFor(nt)[0]; r.value = ""; rerender(); }}>
                      {trig.map((tt) => <option key={tt.value} value={tt.value}>{tt.kind === "section" ? "Section: " : ""}{tt.label}</option>)}
                    </select>
                    <select className="inp" value={r.op} style={{ flex: 1 }}
                      onChange={(e) => { r.op = e.target.value; if (!needsValue(r.op)) r.value = ""; rerender(); }}>
                      {ops.map((op) => <option key={op} value={op}>{op}</option>)}
                    </select>
                  </div>
                  {needsValue(r.op) ? (
                    valField && HAS_OPTS(valField.type) ? (
                      <select className="inp" value={r.value} onChange={(e) => { r.value = e.target.value; rerender(); }}>
                        <option value="">Choose a value…</option>
                        {valField.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : valField && valField.type === "toggle" ? (
                      <select className="inp" value={r.value} onChange={(e) => { r.value = e.target.value; rerender(); }}>
                        <option value="">Choose…</option><option value="Yes">Yes</option><option value="No">No</option>
                      </select>
                    ) : (
                      <input className="inp" placeholder="value" value={r.value} onChange={(e) => { r.value = e.target.value; rerender(); }} />
                    )
                  ) : null}
                  <button className="del" onClick={() => { c.rules.splice(i, 1); if (!c.rules.length) owner[keyName] = null; rerender(); }}>Remove</button>
                </div>
              );
            })}
            <button className="ghost" style={{ width: "100%" }} onClick={addRule}><Svg n="plus" w={13} /> Add rule</button>
          </>
        )}
      </div>
    );
  };

  const renderProps = () => {
    if (!sel || sel.kind === "note") {
      return (
        <div className="pane props">
          <div className="phead"><div className="pt">Note type</div><div className="ps">Title & description</div></div>
          <div className="pbody">
            <div className="field"><label>Title</label>
              <input className="inp" value={M.name} placeholder="Template title" onChange={(e) => { M.name = e.target.value; rerender(); }} /></div>
            <div className="field"><label>Description <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span></label>
              <textarea className="inp" rows={4} value={M.desc} placeholder="What is this note type for?" onChange={(e) => { M.desc = e.target.value; rerender(); }} /></div>
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--line)", fontSize: 12.5, color: "var(--muted)" }}>
              Select a field or section on the canvas to edit its properties here.
            </div>
          </div>
        </div>
      );
    }
    if (sel.kind === "section") {
      const sec = M.sections.find((s) => s.id === sel.id);
      if (!sec) return <div className="pane props" />;
      const idx = M.sections.findIndex((s) => s.id === sec.id);
      const isImg = sec.icon && sec.icon.indexOf("img:") === 0;
      return (
        <div className="pane props">
          <div className="phead"><div className="pt">Section</div><div className="ps">Card settings</div></div>
          <div className="pbody">
            <div className="spanpick" style={{ marginBottom: 14 }}>
              <button disabled={idx === 0} onClick={() => moveSection(sec.id, -1)}><Svg n="arrowUp" w={14} /></button>
              <button disabled={idx === M.sections.length - 1} onClick={() => moveSection(sec.id, 1)}><Svg n="arrowDown" w={14} /></button>
              <button onClick={() => duplicateSection(sec.id)}><Svg n="copy" w={14} /></button>
              <button onClick={() => deleteSection(sec.id)} style={{ color: "var(--danger)" }}><Svg n="trash" w={14} /></button>
            </div>
            <div className="field"><label>Title</label>
              <input className="inp" value={sec.title} onChange={(e) => { sec.title = e.target.value; rerender(); }} /></div>
            <div className="field"><label>Icon</label>
              <div className="iconpick">
                {SECTION_ICONS.map((ic) => (
                  <button key={ic} aria-pressed={!isImg && sec.icon === ic} onClick={() => { sec.icon = ic; rerender(); }}><Svg n={ic} w={15} /></button>
                ))}
                <button aria-pressed={isImg} title="Upload a custom icon" onClick={() => document.getElementById("nt-secicon-file").click()}>
                  {isImg ? <img src={sec.icon.slice(4)} alt="" /> : <Svg n="upload" w={15} />}
                </button>
              </div>
              <input id="nt-secicon-file" type="file" accept="image/png,image/jpeg,image/svg+xml" hidden
                onChange={(e) => { const file = e.target.files?.[0]; if (!file) return; const r = new FileReader(); r.onload = () => { sec.icon = "img:" + r.result; rerender(); }; r.readAsDataURL(file); e.target.value = ""; }} />
              {isImg ? <button className="inp" style={{ marginTop: 8, cursor: "pointer", textAlign: "left", color: "var(--muted)" }}
                onClick={() => { sec.icon = SECTION_ICONS[0]; rerender(); }}>Remove custom icon</button>
                : <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>Upload a custom icon (PNG or SVG).</div>}
            </div>
            {conditionalEditor({ owner: sec, keyName: "cond", target: { kind: "section", id: sec.id } })}
          </div>
        </div>
      );
    }
    // field
    const { f } = findField(sel.id);
    if (!f) return <div className="pane props" />;
    const others = allFields().filter((x) => x.id !== f.id && ["number", "calc"].includes(x.type));
    return (
      <div className="pane props">
        <div className="phead"><div className="pt">{TYPE_META[f.type]?.label || f.type}</div><div className="ps">Field settings</div></div>
        <div className="pbody">
          <div className="field"><label>Label</label>
            <input className="inp" value={f.label} onChange={(e) => setField(f.id, { label: e.target.value })} /></div>
          {isDup(f) ? <div className="calcout" style={{ color: "var(--danger)" }}><Svg n="alert" w={12} /> Another field in this section uses this name. Names must be unique within a section.</div> : null}
          {["client", "staff", "facility"].includes(f.type) ? (
            (() => {
              // The first field of each of these types is the note's canonical
              // one — it keeps the fixed storage key every downstream consumer
              // reads. Extras are independent questions; say which is which so
              // an author isn't guessing why one of them drives the care plan.
              const primary = allFields().find((x) => x.type === f.type)?.id === f.id;
              const what = TYPE_META[f.type]?.label || f.type;
              return (
                <div className="calcout">
                  {primary
                    ? `Main ${what} for the note — saved as “${keyOf(f)}”. The notes list, tables, reports and the care plan all read this one.`
                    : `Extra ${what} field — saved as “${keyOf(f)}”, holding its own separate selection. The first ${what} field stays the note's main one.`}
                </div>
              );
            })()
          ) : null}
          {!["toggle", "chips", "client", "staff", "facility", "notifications", "calc", "careplan"].includes(f.type) ? (
            <div className="field"><label>Placeholder</label>
              <input className="inp" value={f.placeholder || ""} onChange={(e) => setField(f.id, { placeholder: e.target.value })} /></div>
          ) : null}
          <div className="field"><label>Help text</label>
            <input className="inp" value={f.help || ""} onChange={(e) => setField(f.id, { help: e.target.value })} /></div>
          <div className="field"><label>Width</label>
            <div className="spanpick">
              {[[4, "⅓"], [6, "½"], [12, "Full"]].map(([w, lbl]) => (
                <button key={w} aria-pressed={f.span === w} onClick={() => setField(f.id, { span: w })}>{lbl}</button>
              ))}
            </div>
          </div>
          <div className="switch"><label>Required</label>
            <input type="checkbox" className="tog" checked={!!f.required} onChange={(e) => setField(f.id, { required: e.target.checked })} /></div>

          {HAS_OPTS(f.type) ? (
            <div className="psec"><div className="ph"><Svg n="drop" w={13} /> Options</div>
              <div className="opts">
                {f.options.map((o, i) => (
                  <div className="optrow" key={i}>
                    <input className="inp" value={o} onChange={(e) => { f.options[i] = e.target.value; rerender(); }} />
                    <button onClick={() => { f.options.splice(i, 1); rerender(); }}><Svg n="trash" w={13} /></button>
                  </div>
                ))}
              </div>
              <button className="addopt" onClick={() => { f.options.push("Option " + (f.options.length + 1)); rerender(); }}><Svg n="plus" w={13} /> Add option</button>
            </div>
          ) : null}

          {f.type === "careplan" ? (
            (() => {
              // Either part, or both — so these are independent toggles. The
              // last selected one can't be turned off: a care plan field with
              // nothing to document would render an empty block on the note.
              const parts = carePlanPartsOf(f);
              const toggle = (key) => {
                const next = parts.includes(key)
                  ? parts.filter((p) => p !== key)
                  : [...parts, key];
                if (!next.length) return;
                setField(f.id, { carePlanParts: next });
              };
              return (
                <div className="psec"><div className="ph"><Svg n="careplan" w={13} /> Documents</div>
                  <div className="spanpick">
                    {CARE_PLAN_PARTS.map((p) => {
                      const on = parts.includes(p.key);
                      return (
                        <button
                          key={p.key}
                          aria-pressed={on}
                          title={on && parts.length === 1 ? "Select at least one" : p.label}
                          onClick={() => toggle(p.key)}
                        >
                          {p.short}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--muted)", margin: "8px 0 0" }}>
                    {parts.length === CARE_PLAN_PARTS.length
                      ? "The note documents against both the client's active Support Plan outcomes and Essential Supports plan."
                      : CARE_PLAN_PARTS.find((p) => p.key === parts[0])?.hint}{" "}
                    Answering "No" on the note hides the block
                    {parts.length > 1 ? "s" : ""}.
                  </div>
                </div>
              );
            })()
          ) : null}

          {f.type === "calc" ? (
            <div className="psec"><div className="ph">ƒ Calculation</div>
              <div className="field"><label>Formula</label>
                <textarea className="inp" placeholder="{Field A} + {Field B}" value={f.calc?.formula || ""}
                  onChange={(e) => { f.calc = f.calc || {}; f.calc.formula = e.target.value; rerender(); }} /></div>
              <div style={{ fontSize: 11.5, color: "var(--muted)", margin: "-8px 0 8px" }}>Reference numeric fields with {"{curly braces}"}. Use + − × ÷ ( ).</div>
              <div className="field"><label>Insert field</label>
                <select className="inp" value="" onChange={(e) => { if (!e.target.value) return; f.calc = f.calc || { formula: "" }; f.calc.formula = (f.calc.formula ? f.calc.formula + " " : "") + "{" + e.target.value + "}"; rerender(); }}>
                  <option value="">{others.length ? "Choose a numeric field…" : "No numeric fields yet"}</option>
                  {others.map((x) => <option key={x.id} value={keyOf(x)}>{keyOf(x)}</option>)}
                </select></div>
            </div>
          ) : null}

          {conditionalEditor({ owner: f, keyName: "conditional", target: { kind: "field", f } })}
        </div>
      </div>
    );
  };

  /* ── preview ──
   * Renders through the real StudioSectionsForm (the same component the note
   * create drawer uses), so preview fields are pixel-identical to what staff
   * see when creating a note — no hand-built mock controls to drift apart. */
  const renderPreview = () => (
    <div className="pane" style={{ gridColumn: "1 / -1" }}>
      <div className="pvstage">
        <div className="drawer">
          <div className="dhead">Add Note</div>
          <div className="dbody">
            {/* Top "Note Type" selector — matches the create drawer, where the
                note type is chosen first. In preview it's fixed to this template. */}
            <div className="pvcard" style={{ padding: "14px 16px" }}>
              <div className="pvf">
                <label>Note Type</label>
                <button
                  type="button"
                  className="pvctl"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    cursor: "default",
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {M.name || "Untitled note type"}
                  </span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#667085"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flex: "none" }}
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>
              </div>
            </div>
            <StudioSectionsForm
              // Same resolved storage keys the save writes, so the preview binds
              // same-named fields in different sections to separate values.
              sections={M.sections.map((s) => ({
                ...s,
                fields: (s.fields || []).map((f) => ({ ...f, key: keyOf(f) })),
              }))}
              form={previewForm}
              classes={noteModalClasses}
              view={false}
            />
          </div>
        </div>
      </div>
    </div>
  );

  /* ── settings step ── */
  const renderSettings = () => {
    const s = M.settings;
    const allOn = s.allApprovers;
    const expanded = apprExpanded || !allOn;

    // ── Availability ──
    // Two independent controls:
    //   • the "Available to all staff" toggle is a check-all / uncheck-all —
    //     on clears every exclusion, off drops to an empty allow-list.
    //   • each staff row toggles only that one person; it never moves the
    //     switch above.
    // Which list a row writes to depends on the mode, so the toggle keeps
    // meaning what it says:
    //   allStaff on  → everyone EXCEPT settings.staffExcluded (new hires
    //                  included automatically, minus anyone unchecked here)
    //   allStaff off → ONLY settings.staff (an explicit allow-list)
    const allStaffOn = s.allStaff !== false;
    const staffIds = staffList.map((x) => x.id);
    const selectedStaff = s.staff || [];
    const excludedStaff = s.staffExcluded || [];
    const staffOn = (id) =>
      allStaffOn ? !excludedStaff.includes(id) : selectedStaff.includes(id);
    const checkedCount = allStaffOn
      ? staffIds.filter((id) => !excludedStaff.includes(id)).length
      : selectedStaff.length;
    const toggleStaff = (id) => {
      const key = allStaffOn ? "staffExcluded" : "staff";
      const next = [...(s[key] || [])];
      const i = next.indexOf(id);
      // In "all staff" mode the list holds who's OFF, so unchecking adds;
      // in allow-list mode it holds who's ON, so unchecking removes.
      i >= 0 ? next.splice(i, 1) : next.push(id);
      s[key] = next;
      rerender();
    };

    return (
      <div className="stepwrap">
        <div className="stepinner">
          <div className="step-title">Note type settings</div>

          <div className="setgroup">
            <div className="setg-h">Approval</div>
            <div className="set-switch"><div><div className="sl">Require approval</div></div>
              <input type="checkbox" className="tog" checked={s.approvalRequired} onChange={(e) => { s.approvalRequired = e.target.checked; rerender(); }} /></div>
            {s.approvalRequired ? (
              <>
                <div className="set-sub">Who can approve?</div>
                <div className="adminrow" style={{ borderBottom: "1px solid var(--line)" }}
                  onClick={() => { s.allApprovers = true; s.approvers = []; rerender(); }}>
                  <span className={`abox ${allOn ? "on" : ""}`}>{allOn ? <Svg n="check" w={12} s={3} /> : null}</span>
                  <span className="an" style={{ flex: 1, fontWeight: 600 }}>All admins</span>
                </div>
                <div className="adminrow" onClick={() => setApprExpanded((v) => !v)}>
                  <span className="an" style={{ flex: 1, fontWeight: 600, color: "var(--muted)" }}>Select specific admins</span>
                  <Svg n="chev" w={16} />
                </div>
                {expanded ? admins.map((a) => {
                  const on = !allOn && s.approvers.includes(a.id);
                  return (
                    <div className="adminrow" key={a.id} onClick={() => {
                      s.allApprovers = false;
                      const i = s.approvers.indexOf(a.id); i >= 0 ? s.approvers.splice(i, 1) : s.approvers.push(a.id);
                      rerender();
                    }}>
                      <span className={`abox ${on ? "on" : ""}`}>{on ? <Svg n="check" w={12} s={3} /> : null}</span>
                      <span className="av">{initials(a.name)}</span>
                      <span style={{ flex: 1 }}><span className="an" style={{ display: "block" }}>{a.name}</span>{a.role ? <span className="ar">{a.role}</span> : null}</span>
                    </div>
                  );
                }) : null}
                {expanded && !allOn && !s.approvers.length ? <div className="approvewarn">Select at least one admin, otherwise no one will be able to approve these notes.</div> : null}
              </>
            ) : null}
          </div>

          <div className="setgroup">
            <div className="setg-h">Availability</div>
            <div className="set-switch">
              <div>
                <div className="sl">Available to all staff</div>
                <div className="sd">
                  {allStaffOn
                    ? excludedStaff.length
                      ? `Every staff member except ${excludedStaff.length} unchecked below.`
                      : "Every active staff member can use this note type."
                    : `Only the ${selectedStaff.length} staff checked below can use this note type.`}
                </div>
              </div>
              <input
                type="checkbox"
                className="tog"
                checked={allStaffOn}
                onChange={(e) => {
                  // Check-all / uncheck-all: on = nobody excluded,
                  // off = empty allow-list to pick from.
                  s.allStaff = e.target.checked;
                  if (e.target.checked) s.staffExcluded = [];
                  else s.staff = [];
                  rerender();
                }}
              />
            </div>
            <div className="set-sub">Who can use this note type?</div>
            {staffList.length ? (
              <div className="stafflist">
                {staffList.map((a) => {
                  const on = staffOn(a.id);
                  return (
                    <div className="adminrow" key={a.id} onClick={() => toggleStaff(a.id)}>
                      <span className={`abox ${on ? "on" : ""}`}>{on ? <Svg n="check" w={12} s={3} /> : null}</span>
                      <span className="av">{initials(a.name)}</span>
                      <span style={{ flex: 1 }}><span className="an" style={{ display: "block" }}>{a.name}</span>{a.role ? <span className="ar">{a.role}</span> : null}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="approvewarn">No active staff found for this organization.</div>
            )}
            {staffList.length && !checkedCount ? (
              <div className="approvewarn">No staff checked, so no one will see this note type. Check at least one staff member, or turn “Available to all staff” back on.</div>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="nt-studio">
      <style>{STUDIO_CSS}</style>

      <div className="bar">
        <button className="backbtn" onClick={back}><Svg n="chev" w={15} /> Back</button>
        <div className="spacer" />
        {step === "build" ? (
          <div className="seg">
            <button aria-pressed={mode === "build"} onClick={() => setMode("build")}><Svg n="careplan" w={14} /> Build</button>
            <button aria-pressed={mode === "preview"} onClick={() => { setMode("preview"); }}><Svg n="eye" w={14} /> Preview</button>
          </div>
        ) : null}
        <div className="spacer" />
        {!readOnly ? (
          <button className="primary" disabled={saving} onClick={() => (step === "settings" ? save() : goSettings())}>
            {step === "settings" ? (saving ? "Saving…" : "Save Template") : "Save & Next"}
          </button>
        ) : null}
      </div>

      {step === "settings" ? renderSettings() : (
        <div className={`work ${mode === "preview" ? "preview" : ""}`}>
          {mode === "preview" ? renderPreview() : readOnly ? (
            <>
              <div className="disabledwrap">{renderPalette()}</div>
              {renderCanvasReadOnly()}
              <div className="disabledwrap">{renderProps()}</div>
            </>
          ) : (
            <>{renderPalette()}{renderCanvas()}{renderProps()}</>
          )}
        </div>
      )}

      {toast ? <div className="toast"><Svg n="check" w={17} /> {toast}</div> : null}
    </div>
  );
};

export default NoteTypeStudio;
